import { useState } from 'react';
import { colors } from '../constants/colors';
import { X, Loader2, Package, CheckCircle, ArrowLeft, Printer, Download } from 'lucide-react';
import {
  createShipment,
  getShipmentRates,
  updateShipmentItems,
  buyShipmentLabel,
  deleteShipment,
} from '../api/client';
import type { ItemSaleWithPayment, ShippingRateQuote, BuyLabelResponse } from '../types';
import { PackagingSelector, usePackagingState, isPackagingValid, buildRateData } from './PackagingSelector';
import { Img } from './Img';

const CARRIER_LOGOS: Record<string, string> = {
  USPS: '/img/logos/carrier-usps.svg',
  UPS: '/img/logos/carrier-ups.svg',
  FedEx: '/img/logos/carrier-fx.svg',
};

const SERVICE_DISPLAY_NAMES: Record<string, Record<string, string>> = {
  USPS: {
    GroundAdvantage: 'Ground Advantage',
    Priority: 'Priority Mail',
    Express: 'Priority Mail Express',
    First: 'First Class',
    ParcelSelect: 'Parcel Select',
  },
  UPS: {
    Ground: 'Ground',
    '3DaySelect': '3-Day Select',
    '2ndDayAir': '2nd Day Air',
    '2ndDayAirAM': '2nd Day Air AM',
    NextDayAirSaver: 'Next Day Air Saver',
    NextDayAir: 'Next Day Air',
    NextDayAirEarlyAM: 'Next Day Air Early AM',
    UPSGroundsaverGreaterThan1lb: 'Ground Saver',
  },
  FedEx: {
    FEDEX_GROUND: 'Ground',
    FEDEX_2_DAY: '2 Day',
    FEDEX_2_DAY_AM: '2 Day AM',
    FEDEX_EXPRESS_SAVER: 'Express Saver',
    PRIORITY_OVERNIGHT: 'Priority Overnight',
    STANDARD_OVERNIGHT: 'Standard Overnight',
    FIRST_OVERNIGHT: 'First Overnight',
    SMART_POST: 'SmartPost',
  },
};

function getServiceDisplayName(carrier: string, service: string): string {
  return SERVICE_DISPLAY_NAMES[carrier]?.[service]
    ?? service.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

interface ShipLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  orderId: string;
  unshippedItems: ItemSaleWithPayment[];
  initialWeightOz?: string;
  initialLengthIn?: string;
  initialWidthIn?: string;
  initialHeightIn?: string;
}

export function ShipLabelModal({
  isOpen,
  onClose,
  onComplete,
  orderId,
  unshippedItems,
  initialWeightOz = '',
  initialLengthIn = '',
  initialWidthIn = '',
  initialHeightIn = '',
}: ShipLabelModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(unshippedItems.map(i => i.id))
  );
  const [pkgState, setPkgState] = usePackagingState(initialWeightOz, initialLengthIn, initialWidthIn, initialHeightIn);
  const [rates, setRates] = useState<ShippingRateQuote[]>([]);
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [isBuyingLabel, setIsBuyingLabel] = useState(false);
  const [labelError, setLabelError] = useState<string | null>(null);
  const [purchaseResult, setPurchaseResult] = useState<BuyLabelResponse | null>(null);
  const [lastFetchedKey, setLastFetchedKey] = useState('');
  const [createdShipmentId, setCreatedShipmentId] = useState<string | null>(null);
  const [lastFetchedItemIds, setLastFetchedItemIds] = useState('');

  if (!isOpen) return null;

  const dimensionsKey = JSON.stringify(buildRateData(pkgState));
  const selectedIdsKey = [...selectedIds].sort().join(',');
  const dimensionsValid = isPackagingValid(pkgState);
  const dimensionsChanged = dimensionsValid && (dimensionsKey !== lastFetchedKey || selectedIdsKey !== lastFetchedItemIds);
  const selectedRate = rates.find(r => r.rateId === selectedRateId);

  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === unshippedItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(unshippedItems.map(i => i.id)));
    }
  };

  const handleGetRates = async () => {
    if (!dimensionsValid) {
      setLabelError(pkgState.mode === 'carrier'
        ? 'Please select a carrier package and enter weight'
        : 'Please fill in all package dimensions and weight');
      return;
    }
    if (selectedIds.size === 0) {
      setLabelError('Please select at least one item');
      return;
    }
    try {
      setIsLoadingRates(true);
      setLabelError(null);
      setRates([]);
      setSelectedRateId(null);

      let shipId = createdShipmentId;
      if (!shipId) {
        const shipment = await createShipment(orderId, [...selectedIds]);
        shipId = shipment.id;
        setCreatedShipmentId(shipId);
      } else {
        await updateShipmentItems(shipId, [...selectedIds]);
      }

      const response = await getShipmentRates(shipId, buildRateData(pkgState));
      setRates(response.rates);
      setLastFetchedKey(dimensionsKey);
      setLastFetchedItemIds(selectedIdsKey);
      if (response.rates.length === 0) {
        setLabelError('No rates available for this package. Check dimensions and origin address.');
      }
    } catch (err) {
      setLabelError(err instanceof Error ? err.message : 'Failed to get rates');
    } finally {
      setIsLoadingRates(false);
    }
  };

  const handleBuyLabel = async () => {
    if (!selectedRateId || !createdShipmentId) return;
    try {
      setIsBuyingLabel(true);
      setLabelError(null);
      const result = await buyShipmentLabel(createdShipmentId, { rateId: selectedRateId });
      setPurchaseResult(result);
      onComplete();
    } catch (err) {
      setLabelError(err instanceof Error ? err.message : 'Failed to buy label');
    } finally {
      setIsBuyingLabel(false);
    }
  };

  const handlePurchaseAnother = () => {
    setPurchaseResult(null);
    setRates([]);
    setSelectedRateId(null);
    setCreatedShipmentId(null);
    setLastFetchedKey('');
    setLastFetchedItemIds('');
    setLabelError(null);
    // Reset selected items to current unshipped items (already refreshed via onComplete)
    setSelectedIds(new Set(unshippedItems.map(i => i.id)));
  };

  const handleClose = async () => {
    // Clean up the draft shipment if we created one but didn't buy a label
    if (createdShipmentId && !purchaseResult) {
      try {
        await deleteShipment(createdShipmentId);
      } catch {
        // Best-effort cleanup — if it fails the shipment is just in CREATED state
      }
      onComplete(); // Reload order to restore unshipped items
    }
    onClose();
  };

  return (
    <div style={styles.overlay}>
      <div style={purchaseResult ? styles.modalSuccess : styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>{purchaseResult ? 'Label Created' : 'Buy Shipping Label'}</h2>
          <button onClick={handleClose} style={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        {purchaseResult ? (
          <div style={styles.successBody}>
            <div style={styles.successIcon}>
              <CheckCircle size={40} color={colors.status.success} />
              <h3 style={styles.successTitle}>Label purchased successfully</h3>
            </div>

            <div style={styles.successGrid}>
              {/* Left — details */}
              <div style={styles.successDetails}>
                <div style={styles.successRow}>
                  <span style={styles.successLabel}>Service</span>
                  <span style={styles.successValue}>
                    {CARRIER_LOGOS[purchaseResult.carrier] && (
                      <img src={CARRIER_LOGOS[purchaseResult.carrier]} alt={purchaseResult.carrier} style={styles.carrierLogoSmall} />
                    )}
                    {purchaseResult.carrier} — {getServiceDisplayName(purchaseResult.carrier, purchaseResult.service)}
                  </span>
                </div>
                <div style={styles.successRow}>
                  <span style={styles.successLabel}>Tracking</span>
                  <span style={{ ...styles.successValue, fontFamily: 'monospace' }}>{purchaseResult.trackingNumber}</span>
                </div>
                <div style={styles.successRow}>
                  <span style={styles.successLabel}>Cost</span>
                  <span style={styles.successValue}>{formatCurrency(purchaseResult.postageCost)}</span>
                </div>
              </div>

              {/* Right — label actions */}
              <div style={styles.successActions}>
                <a
                  href={purchaseResult.labelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.downloadLabelButton}
                >
                  <Download size={16} />
                  Download Label
                </a>
                <a
                  href={purchaseResult.labelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.printLabelButton}
                  onClick={(e) => {
                    e.preventDefault();
                    const w = window.open(purchaseResult!.labelUrl, '_blank');
                    if (w) {
                      w.addEventListener('load', () => w.print());
                    }
                  }}
                >
                  <Printer size={16} />
                  Print Label
                </a>
              </div>
            </div>

            {unshippedItems.length > 0 && (
              <button style={styles.purchaseAnotherButton} onClick={handlePurchaseAnother}>
                <Package size={16} />
                Buy Another Label
              </button>
            )}

            <button style={styles.backToOrderButton} onClick={handleClose}>
              <ArrowLeft size={16} />
              Back to Order
            </button>
          </div>
        ) : (
          <div style={styles.twoColumnBody}>
            {/* Left column — item selection */}
            <div style={styles.leftColumn}>
              <div style={styles.leftHeader}>
                <span style={styles.leftTitle}>Items to Ship</span>
                <button style={styles.toggleLink} onClick={toggleAll}>
                  {selectedIds.size === unshippedItems.length ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div style={styles.itemList}>
                {unshippedItems.map(item => (
                  <label key={item.id} style={styles.itemRow}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      onChange={() => toggleItem(item.id)}
                      style={styles.checkbox}
                    />
                    {item.itemSnapshot?.imageUrl ? (
                      <Img src={item.itemSnapshot.imageUrl} alt={item.itemSnapshot.name} variant="thumb" renderedWidth={36} blurhash={item.itemSnapshot.blurhash} style={styles.itemThumb} />
                    ) : (
                      <div style={styles.itemThumbPlaceholder}>
                        <Package size={16} color={colors.gray[300]} />
                      </div>
                    )}
                    <div style={styles.itemDetails}>
                      <span style={styles.itemName}>{item.itemSnapshot?.name ?? 'Unknown Item'}</span>
                      <span style={styles.itemPrice}>{formatCurrency(item.winningBid)}</span>
                    </div>
                  </label>
                ))}
              </div>
              <div style={styles.leftFooter}>
                {selectedIds.size} of {unshippedItems.length} items selected
              </div>
            </div>

            {/* Right column — packaging & rates */}
            <div style={styles.rightColumn}>
              <PackagingSelector state={pkgState} onChange={(s) => { setPkgState(s); setRates([]); setLastFetchedKey(''); }} />

              <button
                style={{
                  ...styles.getRatesButton,
                  ...(((!dimensionsValid || !dimensionsChanged) && !isLoadingRates) ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                }}
                onClick={handleGetRates}
                disabled={!dimensionsValid || !dimensionsChanged || isLoadingRates}
              >
                {isLoadingRates ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Getting Rates...
                  </>
                ) : (
                  rates.length > 0 ? 'Update Rates' : 'Get Rates'
                )}
              </button>

              <div style={{ marginBottom: 16 }} />

              {labelError && <div style={styles.errorBox}>{labelError}</div>}

              {rates.length > 0 && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Select a Rate</h3>
                  <div style={styles.ratesList}>
                    {rates.map(rate => (
                      <button
                        key={rate.rateId}
                        style={{
                          ...styles.rateCard,
                          ...(selectedRateId === rate.rateId ? styles.rateCardSelected : {}),
                        }}
                        onClick={() => setSelectedRateId(rate.rateId)}
                      >
                        <div style={styles.rateInfo}>
                          <div style={styles.rateCarrierRow}>
                            {CARRIER_LOGOS[rate.carrier] && (
                              <img src={CARRIER_LOGOS[rate.carrier]} alt={rate.carrier} style={styles.rateCarrierLogo} />
                            )}
                            <span style={styles.rateCarrier}>{rate.carrier}</span>
                          </div>
                          <span style={styles.rateService}>{getServiceDisplayName(rate.carrier, rate.service)}</span>
                          {rate.estimatedDays > 0 && (
                            <span style={styles.rateDelivery}>
                              ~{rate.estimatedDays} day{rate.estimatedDays !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <span style={styles.rateCost}>{formatCurrency(rate.cost)}</span>
                      </button>
                    ))}
                  </div>

                  <p style={styles.costInfoText}>
                    The cost of the shipping label will be deducted from your account balance.
                  </p>

                  <button
                    style={{
                      ...styles.buyButton,
                      ...((!selectedRateId || isBuyingLabel || selectedIds.size === 0) ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                    }}
                    onClick={handleBuyLabel}
                    disabled={!selectedRateId || isBuyingLabel || selectedIds.size === 0}
                  >
                    {isBuyingLabel ? (
                      <>
                        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                        Purchasing...
                      </>
                    ) : (
                      <>Buy Label{selectedRate ? ` — ${formatCurrency(selectedRate.cost)}` : ''}</>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '780px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    flexDirection: 'column',
  },
  modalSuccess: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '640px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${colors.gray[300]}`,
    flexShrink: 0,
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    color: colors.ink.black,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[600],
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  twoColumnBody: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    overflow: 'hidden',
    flex: 1,
    minHeight: 0,
  },
  leftColumn: {
    borderRight: `1px solid ${colors.gray[300]}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  leftHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px 8px',
  },
  leftTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  toggleLink: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    color: colors.brand.blue,
    fontWeight: 500,
    padding: 0,
  },
  itemList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 20px',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 0',
    borderBottom: `1px solid ${colors.gray[200]}`,
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  itemThumb: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    objectFit: 'cover',
    flexShrink: 0,
  },
  itemThumbPlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: colors.gray[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  itemDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    overflow: 'hidden',
    flex: 1,
  },
  itemName: {
    fontSize: '13px',
    fontWeight: 500,
    color: colors.ink.black,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemPrice: {
    fontSize: '12px',
    color: colors.gray[600],
  },
  leftFooter: {
    padding: '12px 20px',
    borderTop: `1px solid ${colors.gray[300]}`,
    fontSize: '12px',
    color: colors.gray[600],
    fontWeight: 500,
    flexShrink: 0,
  },
  rightColumn: {
    padding: '20px 24px',
    overflowY: 'auto',
  },
  section: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 10px 0',
  },
  weightField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxWidth: '160px',
  },
  dimLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[600],
  },
  dimInput: {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.ink.black,
    boxSizing: 'border-box',
  },
  dimGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '10px',
    marginBottom: '10px',
  },
  dimField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  getRatesButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    width: '100%',
    padding: '10px 16px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.brand.blue,
    backgroundColor: colors.brand.blue10,
    border: `1px solid ${colors.brand.blue}`,
    borderRadius: '8px',
    cursor: 'pointer',
  },
  errorBox: {
    padding: '10px 12px',
    marginBottom: '12px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '13px',
  },
  ratesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px',
    maxHeight: '240px',
    overflowY: 'auto',
  },
  rateCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: colors.gray[100],
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.gray[300],
    borderRadius: '10px',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
  },
  rateCardSelected: {
    backgroundColor: colors.brand.blue10,
    borderColor: colors.brand.blue,
  },
  rateInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  rateCarrierRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  rateCarrierLogo: {
    height: '16px',
    width: 'auto',
    objectFit: 'contain',
  },
  rateCarrier: {
    fontSize: '13px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  rateService: {
    fontSize: '12px',
    color: colors.gray[600],
  },
  rateDelivery: {
    fontSize: '11px',
    color: colors.gray[600],
  },
  rateCost: {
    fontSize: '15px',
    fontWeight: 700,
    color: colors.ink.black,
  },
  costInfoText: {
    fontSize: '12px',
    color: colors.gray[600],
    textAlign: 'center',
    margin: '0 0 12px 0',
    lineHeight: 1.4,
  },
  buyButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  successBody: {
    padding: '24px',
  },
  successIcon: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px',
  },
  successTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: colors.ink.black,
    margin: 0,
  },
  successGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '16px',
    alignItems: 'start',
    marginBottom: '16px',
  },
  successDetails: {
    backgroundColor: colors.gray[100],
    borderRadius: '10px',
    padding: '14px 16px',
  },
  successRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px',
    padding: '6px 0',
  },
  successLabel: {
    color: colors.gray[600],
    fontWeight: 500,
  },
  successValue: {
    color: colors.ink.black,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  carrierLogoSmall: {
    height: '16px',
    width: 'auto',
    objectFit: 'contain',
  },
  successActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: '150px',
  },
  downloadLabelButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: '#22C55E',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    textDecoration: 'none',
    boxSizing: 'border-box',
  },
  printLabelButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[700],
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '10px',
    cursor: 'pointer',
    textDecoration: 'none',
    boxSizing: 'border-box',
  },
  purchaseAnotherButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.brand.blue,
    backgroundColor: colors.brand.blue10,
    border: `1px solid ${colors.brand.blue}`,
    borderRadius: '10px',
    cursor: 'pointer',
    marginBottom: '8px',
  },
  backToOrderButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.gray[700],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
};
